import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useTenant } from '@/contexts/TenantContext';

export interface Document {
  id: string;
  tenantId: string;
  projectId: string | null;
  title: string;
  description: string | null;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadedBy: string;
  createdAt: string;
  updatedAt: string;
  project?: {
    id: string;
    name: string;
    color: string;
  } | null;
  uploader: {
    id: string;
    fullName: string | null;
    avatarUrl: string | null;
  };
}

export interface CreateDocumentDto {
  title: string;
  description?: string;
  projectId?: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
}

export interface UpdateDocumentDto {
  title?: string;
  description?: string;
}

interface DocumentsFilters {
  projectId?: string;
}

export function useDocuments(filters: DocumentsFilters = {}) {
  const { currentTenant } = useTenant();

  const params = new URLSearchParams();
  if (filters.projectId) params.set('projectId', filters.projectId);

  const queryString = params.toString();
  const path = `/documents${queryString ? `?${queryString}` : ''}`;

  const query = useQuery({
    queryKey: ['documents', filters, currentTenant?.id],
    queryFn: () => api.get<Document[]>(path),
    enabled: !!currentTenant,
  });

  return query;
}

export function useDocument(id: string) {
  const { currentTenant } = useTenant();

  return useQuery({
    queryKey: ['document', id, currentTenant?.id],
    queryFn: () => api.get<Document>(`/documents/${id}`),
    enabled: !!currentTenant && !!id,
  });
}

export function useCreateDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDocumentDto) => api.post<Document>('/documents', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}

export function useUpdateDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDocumentDto }) =>
      api.put<Document>(`/documents/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['document', variables.id] });
    },
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/documents/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}
